import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LuCheck, LuTrash2, LuBell } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useActiveFlat } from "../lib/active-flat";
import { useAuth } from "../lib/auth";
import type { Chore, ChoreStatus } from "../lib/types";
import { Avatar } from "../components/Avatar";
import {
  Button,
  Card,
  CenteredSpinner,
  EmptyState,
  ErrorBanner,
  IconButton,
  Input,
  Label,
  NlpDateInput,
  Pill,
  SectionHeader,
  Select,
} from "../components/ui";
import { formatDate, formatRelativeTime } from "../lib/format";

export function ChoresPage() {
  const { flat } = useActiveFlat();
  const { user } = useAuth();
  const flatId = flat!.id;

  const [chores, setChores] = useState<Chore[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newAssignee, setNewAssignee] = useState<string>("");
  const [newDue, setNewDue] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get<Chore[]>(`/chore/by-flat/${flatId}`);
      setChores(data);
    } catch (err) {
      setError(errMsg(err, "Couldn't load chores."));
    }
  }, [flatId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateChore = async (id: string, patch: Partial<Pick<Chore, "status" | "assigneeId">>) => {
    try {
      await api.patch(`/chore/${id}`, patch);
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't update chore."));
    }
  };

  const deleteChore = async (id: string) => {
    if (!window.confirm("Delete this chore?")) return;
    try {
      await api.delete(`/chore/${id}`);
      setChores((prev) => prev?.filter((c) => c.id !== id) ?? null);
    } catch (err) {
      setError(errMsg(err, "Couldn't delete chore."));
    }
  };

  const submitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post<Chore>(`/chore/by-flat/${flatId}`, {
        name: newName.trim(),
        assigneeId: newAssignee || undefined,
        dueAt: newDue ? new Date(newDue).toISOString() : undefined,
      });
      setNewName("");
      setNewAssignee("");
      setNewDue("");
      await load();
    } catch (err) {
      setError(errMsg(err, "Couldn't add chore."));
    } finally {
      setAdding(false);
    }
  };

  const grouped = useMemo(() => {
    const groups: Record<ChoreStatus, Chore[]> = { ESCALATED: [], PENDING: [], DONE: [] };
    chores?.forEach((c) => groups[c.status].push(c));
    return groups;
  }, [chores]);

  const monthlyCompletion = useMemo(() => {
    if (!chores) return [];
    const now = new Date();
    const month = now.getMonth();
    const year = now.getFullYear();
    const counts = new Map<string, { name: string; colour: string; count: number }>();
    flat!.members.forEach((m) => {
      counts.set(m.userId, { name: m.user.name ?? "Unknown", colour: m.user.colour, count: 0 });
    });
    for (const c of chores) {
      if (c.source === "AUTO") continue; // appliance chores don't count toward load
      if (c.status !== "DONE" || !c.completedAt || !c.assigneeId) continue;
      const d = new Date(c.completedAt);
      if (d.getMonth() !== month || d.getFullYear() !== year) continue;
      const entry = counts.get(c.assigneeId);
      if (entry) entry.count += 1;
    }
    return Array.from(counts.values());
  }, [chores, flat]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Chores</h1>
        <p className="text-sm text-slate-500">Keep the rota moving.</p>
      </div>

      <ErrorBanner message={error} />

      <Card>
        <SectionHeader title="Add a chore" />
        <form onSubmit={submitNew} className="grid gap-3 sm:grid-cols-[1fr_180px_180px_auto]">
          <div>
            <Label htmlFor="cname">Task</Label>
            <Input
              id="cname"
              placeholder="Take out the bins"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={adding}
            />
          </div>
          <div>
            <Label htmlFor="cassignee">Assignee</Label>
            <Select id="cassignee" value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)} disabled={adding}>
              <option value="">Auto-assign</option>
              {flat!.members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.userId === user!.id ? "Me" : m.user.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="cdue">Due</Label>
            <NlpDateInput value={newDue} onChange={setNewDue} disabled={adding} />
          </div>
          <div className="flex items-end">
            <Button type="submit" loading={adding} className="w-full">
              Add
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <SectionHeader
          title="This month's completions"
          subtitle="Total chores marked done by each flatmate."
        />
        {monthlyCompletion.every((m) => m.count === 0) ? (
          <p className="py-8 text-center text-sm text-slate-500">
            No chores have been completed yet this month.
          </p>
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer>
              <BarChart data={monthlyCompletion} margin={{ top: 4, right: 8, bottom: 4, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: "#64748b" }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {monthlyCompletion.map((entry, idx) => (
                    <Cell key={idx} fill={entry.colour} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {chores === null ? (
        <CenteredSpinner />
      ) : chores.length === 0 ? (
        <EmptyState title="No chores yet" description="Add the first chore above." />
      ) : (
        <div className="space-y-6">
          {grouped.ESCALATED.length > 0 && (
            <ChoreSection
              title="Escalated"
              chores={grouped.ESCALATED}
              tone="rose"
              members={flat!.members}
              onUpdate={updateChore}
              onDelete={deleteChore}
            />
          )}
          {grouped.PENDING.length > 0 && (
            <ChoreSection
              title="Active"
              chores={grouped.PENDING}
              tone="amber"
              members={flat!.members}
              onUpdate={updateChore}
              onDelete={deleteChore}
            />
          )}
          {grouped.DONE.length > 0 && (
            <ChoreSection
              title="Done"
              chores={grouped.DONE}
              tone="emerald"
              members={flat!.members}
              onUpdate={updateChore}
              onDelete={deleteChore}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ChoreSection({
  title,
  chores,
  tone,
  members,
  onUpdate,
  onDelete,
}: {
  title: string;
  chores: Chore[];
  tone: "amber" | "rose" | "emerald";
  members: { userId: string; user: { name: string | null; colour: string } }[];
  onUpdate: (id: string, patch: Partial<Pick<Chore, "status" | "assigneeId">>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        <Pill tone={tone}>{chores.length}</Pill>
      </div>
      <motion.div
        className="space-y-2"
        initial="hidden"
        animate="show"
        variants={{ show: { transition: { staggerChildren: 0.05 } } }}
      >
        <AnimatePresence initial={false}>
          {chores.map((c) => (
            <ChoreRow key={c.id} chore={c} members={members} onUpdate={onUpdate} onDelete={onDelete} />
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}

function ChoreRow({
  chore,
  members,
  onUpdate,
  onDelete,
}: {
  chore: Chore;
  members: { userId: string; user: { name: string | null; colour: string } }[];
  onUpdate: (id: string, patch: Partial<Pick<Chore, "status" | "assigneeId">>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const isDone = chore.status === "DONE";
  const [nudging, setNudging] = useState(false);
  const [nudged, setNudged] = useState(false);

  const nudge = async () => {
    setNudging(true);
    try {
      await api.post(`/chore/${chore.id}/nudge`, {});
      setNudged(true);
      setTimeout(() => setNudged(false), 3000);
    } finally {
      setNudging(false);
    }
  };
  const assigneeSelect = (
    <>
      {chore.assignee && (
        <Avatar
          name={chore.assignee.name}
          colour={chore.assignee.colour}
          size="xs"
          title={chore.assignee.name ?? undefined}
        />
      )}
      <Select
        value={chore.assigneeId ?? ""}
        onChange={(e) =>
          onUpdate(chore.id, { assigneeId: e.target.value || null } as Partial<Chore>)
        }
        className="compact-select w-auto max-w-[9rem] min-w-0 px-2 py-0.5 text-xs"
      >
        <option value="">Unassigned</option>
        {members.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.user.name ?? "Unknown"}
          </option>
        ))}
      </Select>
    </>
  );

  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ duration: 0.2 }}
      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:items-center">
      <button
        onClick={() => onUpdate(chore.id, { status: isDone ? "PENDING" : "DONE" })}
        aria-label={isDone ? "Mark incomplete" : "Mark done"}
        className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition sm:mt-0 ${
          isDone
            ? "border-emerald-500 bg-emerald-500 text-white"
            : "border-slate-300 text-transparent hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600"
        }`}
      >
        <LuCheck className="h-3.5 w-3.5" />
      </button>

      <div className="min-w-0 flex-1">
        <div
          className={`text-sm font-medium ${
            isDone ? "text-slate-400 line-through" : "text-slate-900"
          }`}
        >
          {chore.name}
        </div>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
          {chore.source === "AUTO" && <span className="text-brand-700">Auto-created</span>}
          {chore.dueAt && (
            <span title={formatDate(chore.dueAt)}>Due {formatRelativeTime(chore.dueAt)}</span>
          )}
          {chore.completedAt && <span>Completed {formatRelativeTime(chore.completedAt)}</span>}
        </div>
        {/* Assignee shown below name on mobile only */}
        <div className="mt-1.5 flex items-center gap-2 sm:hidden">{assigneeSelect}</div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {/* Assignee inline on desktop only */}
        <div className="hidden items-center gap-2 sm:flex">{assigneeSelect}</div>
        {!isDone && chore.assigneeId && (
          <IconButton
            icon={<LuBell className="h-4 w-4" />}
            label={nudged ? "Nudged!" : "Nudge assignee"}
            onClick={nudge}
            disabled={nudging || nudged}
            variant="warn"
            active={nudged}
          />
        )}
        <IconButton
          icon={<LuTrash2 className="h-4 w-4" />}
          label="Delete chore"
          onClick={() => onDelete(chore.id)}
          variant="danger"
        />
      </div>
    </motion.div>
  );
}
